/* eslint-disable react/jsx-curly-newline */
import { FunctionComponent } from 'preact';
import { useDetectDevice } from 'hooks';
import { PudoLocation } from '@pperform/pp-data-mappers';
import {
  CardContainer,
  MapViewContainer,
  DropOffInfoContainer,
} from './DropOffCard.styles';
import { MapView } from '../MapView';
import { DropOffCardButton } from './DropOffCardButton';
import { DropOffCardPanel } from './DropOffCardPanel';

export interface DropOffCardProps {
  dropOff: PudoLocation;
  expanded?: boolean;
  onDropOffClick?: (dropOffId: string) => void;
  onSelectDropOff?: (dropOff: PudoLocation) => void;
  isViewOnly?: boolean;
  isShowDropoffMap?: boolean;
  isHeaderOnly?: boolean;
}

/**
 * DropOffCard component that maintains backward compatibility with existing code
 * while using the new DropOffCardButton and DropOffCardPanel components internally
 */
const DropOffCard: FunctionComponent<DropOffCardProps> = ({
  dropOff,
  expanded = false,
  onDropOffClick,
  onSelectDropOff,
  isViewOnly = false,
  isShowDropoffMap = false,
  isHeaderOnly = false,
}): JSX.Element => {
  const { isMobile } = useDetectDevice();
  const { pudoLocationId } = dropOff;
  const { lat, lng } = dropOff;
  const center = { lat, lng };

  // For the accordion button case (used in DropOffList)
  if (isHeaderOnly) {
    return (
      <CardContainer
        data-testid="drop-off-card-container"
        isMobile={isMobile}
        expanded={false}
        showArrow={!isViewOnly}
        isShowDropoffMap={isShowDropoffMap}
      >
        <DropOffInfoContainer>
          <DropOffCardButton
            dropOff={dropOff}
            isViewOnly={isViewOnly}
            isShowDropoffMap={isShowDropoffMap}
            expanded={false}
          />
        </DropOffInfoContainer>
      </CardContainer>
    );
  }

  // For use with expanded=true (as in ConfirmDropOffLayout)
  if (expanded) {
    return (
      <CardContainer
        data-testid="drop-off-card-container"
        isMobile={isMobile}
        expanded
        showArrow={!isViewOnly}
        isShowDropoffMap={isShowDropoffMap}
        onClick={() => !isViewOnly && onDropOffClick?.(pudoLocationId)}
      >
        <DropOffInfoContainer>
          <DropOffCardButton
            dropOff={dropOff}
            isViewOnly={isViewOnly}
            isShowDropoffMap={isMobile && isShowDropoffMap}
            expanded
          />

          <DropOffCardPanel
            dropOff={dropOff}
            onSelectDropOff={onSelectDropOff}
            isViewOnly={isViewOnly}
          />
        </DropOffInfoContainer>

        {isShowDropoffMap && !isMobile && (
          <MapViewContainer data-testid="drop-off-map-view-container">
            <MapView center={center} isDropOffEmpty={!dropOff} isViewOnlyMode />
          </MapViewContainer>
        )}
      </CardContainer>
    );
  }

  // Default collapsed case (non-accordion use)
  return (
    <CardContainer
      data-testid="drop-off-card-container"
      isMobile={isMobile}
      expanded={false}
      showArrow={!isViewOnly}
      isShowDropoffMap={isShowDropoffMap}
      onClick={() => !isViewOnly && onDropOffClick?.(pudoLocationId)}
    >
      <DropOffInfoContainer>
        <DropOffCardButton
          dropOff={dropOff}
          isViewOnly={isViewOnly}
          isShowDropoffMap={isMobile && isShowDropoffMap}
          expanded={false}
        />
      </DropOffInfoContainer>
      {isShowDropoffMap && !isMobile && (
        <MapViewContainer data-testid="drop-off-map-view-container">
          <MapView center={center} isDropOffEmpty={!dropOff} isViewOnlyMode />
        </MapViewContainer>
      )}
    </CardContainer>
  );
};

export { DropOffCard };
